import type { Rect } from "./matrix";
import { applyPreset, createMesh, evalMesh, latticeH, latticeW, recomputeInterior, resizeMesh, warpPoint, type MeshWarp, type PresetName } from "./mesh";
import { parseCmds, segmentPoint, VERB, type Pt } from "./pathcmds";
import { warpPathCmds, type WarpFn } from "./pathwarp";

/**
 * Glue between the document's stored mesh warp ({cols, rows, points} in node-local units,
 * laid over the node's unwarped box) and the mesh maths in mesh.ts.
 */
export interface StoredMesh {
  cols: number;
  rows: number;
  points: readonly (readonly [number, number])[];
}

type Box = { x: number; y: number; w: number; h: number };

/** Avoid a zero-size rest box (zero-height lines etc.): the mesh maths divides by it. */
const safeBox = (b: Box): Box => ({ x: b.x, y: b.y, w: Math.max(b.w, 1e-6), h: Math.max(b.h, 1e-6) });

export function meshFromStored(box: Box, w: StoredMesh): MeshWarp {
  const m = createMesh(safeBox(box), w.cols, w.rows);
  const n = Math.min(w.points.length, latticeW(m) * latticeH(m));
  for (let k = 0; k < n; k++) {
    m.pts[k * 2] = w.points[k][0];
    m.pts[k * 2 + 1] = w.points[k][1];
  }
  return m;
}

export function storedFromMesh(m: MeshWarp): { type: "mesh"; cols: number; rows: number; points: [number, number][] } {
  const points: [number, number][] = [];
  for (let k = 0; k < m.pts.length; k += 2) points.push([round(m.pts[k]), round(m.pts[k + 1])]);
  return { type: "mesh", cols: m.cols, rows: m.rows, points };
}

/** Documents stay small and diff-friendly: 1/1000 of a unit is far below visible precision. */
const round = (v: number) => Math.round(v * 1000) / 1000;

/** The identity mesh over `box`. */
export function restMesh(box: Box, cols: number, rows: number) {
  return storedFromMesh(createMesh(safeBox(box), cols, rows));
}

/** A fresh envelope preset over `box` (bend -1..1). */
export function presetMeshWarp(box: Box, name: PresetName, bend: number, cols: number, rows: number) {
  const m = createMesh(safeBox(box), cols, rows);
  applyPreset(m, name, bend);
  return storedFromMesh(m);
}

/** Change the grid size, keeping the current warp as closely as possible. */
export function resizeStoredMesh(box: Box, w: StoredMesh, cols: number, rows: number) {
  return storedFromMesh(resizeMesh(meshFromStored(box, w), cols, rows));
}

/** Re-derive the interior controls from the patch boundaries (after dragging edges). */
export function withAutoInterior(box: Box, w: StoredMesh) {
  const m = meshFromStored(box, w);
  recomputeInterior(m);
  return storedFromMesh(m);
}

/** Scale a stored lattice (the node was resized by kx, ky about its local origin). */
export function scaleStoredMesh<T extends StoredMesh>(w: T, kx: number, ky = kx): T {
  return { ...w, points: w.points.map(([x, y]) => [x * kx, y * ky]) };
}

/** Warp function (point + Jacobian) for path warping. */
export function meshWarpFn(m: MeshWarp): WarpFn {
  return (x, y) => {
    const s = warpPoint(m, x, y);
    return { x: s.x, y: s.y, j: [s.dxu, s.dxv, s.dyu, s.dyv] };
  };
}

/** True if the lattice is the rest grid (nothing to warp). */
export function isRestMesh(box: Box, w: StoredMesh, eps = 2e-3): boolean {
  const rest = createMesh(safeBox(box), w.cols, w.rows);
  for (let k = 0; k < w.points.length; k++) {
    if (Math.abs(w.points[k][0] - rest.pts[k * 2]) > eps || Math.abs(w.points[k][1] - rest.pts[k * 2 + 1]) > eps) return false;
  }
  return true;
}

const boundsCache = new WeakMap<object, Rect>();

/**
 * Bounds of the warped box: the surface's outer boundary curves, sampled densely. (Folds
 * could push the interior past the boundary; that's rare and only affects the frame.)
 */
export function meshBounds(box: Box, w: StoredMesh): Rect {
  const hit = boundsCache.get(w);
  if (hit) return hit;
  const m = meshFromStored(box, w);
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const add = (u: number, v: number) => {
    const s = evalMesh(m, u, v);
    if (s.x < x0) x0 = s.x;
    if (s.x > x1) x1 = s.x;
    if (s.y < y0) y0 = s.y;
    if (s.y > y1) y1 = s.y;
  };
  const nU = m.cols * 24, nV = m.rows * 24;
  for (let k = 0; k <= nU; k++) {
    add(k / nU, 0);
    add(k / nU, 1);
  }
  for (let k = 0; k <= nV; k++) {
    add(0, k / nV);
    add(1, k / nV);
  }
  const r = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  boundsCache.set(w, r);
  return r;
}

/** Outline of the warped box (its four warped edges), as closed path commands. */
export function meshOutlineCmds(box: Box, w: StoredMesh, perPatch = 16): number[] {
  const m = meshFromStored(box, w);
  const out: number[] = [];
  const nU = m.cols * perPatch, nV = m.rows * perPatch;
  const push = (u: number, v: number, first = false) => {
    const s = evalMesh(m, u, v);
    out.push(first ? VERB.MOVE : VERB.LINE, s.x, s.y);
  };
  for (let k = 0; k <= nU; k++) push(k / nU, 0, k === 0);
  for (let k = 1; k <= nV; k++) push(1, k / nV);
  for (let k = nU - 1; k >= 0; k--) push(k / nU, 1);
  for (let k = nV - 1; k > 0; k--) push(0, k / nV);
  out.push(VERB.CLOSE);
  return out;
}

/**
 * Exact vector warp (spec §5): sample each segment through the mesh and re-fit cubics within
 * `tolerance` (output units). Lines warp to curves too, so straight edges bend with the mesh.
 */
export function warpCmdsThroughMesh(cmds: ArrayLike<number>, box: Box, w: StoredMesh, tolerance = 0.25): number[] {
  return warpPathCmds(cmds, meshWarpFn(meshFromStored(box, w)), { tolerance }).cmds;
}

/**
 * Cheap preview for interactive drags: every segment becomes a polyline through the mesh
 * (no fitting). `spacing` is the target chord length in output units.
 */
export function warpCmdsPreview(cmds: ArrayLike<number>, box: Box, w: StoredMesh, spacing = 2): number[] {
  const m = meshFromStored(box, w);
  const map = (p: Pt) => warpPoint(m, p.x, p.y);
  const out: number[] = [];
  for (const contour of parseCmds(cmds)) {
    const s0 = map(contour.start);
    out.push(VERB.MOVE, s0.x, s0.y);
    for (const seg of contour.segments) {
      // Length estimate from a coarse pass picks the sample count.
      let len = 0, prev = map(seg.p0);
      for (let k = 1; k <= 4; k++) {
        const q = map(segmentPoint(seg, k / 4));
        len += Math.hypot(q.x - prev.x, q.y - prev.y);
        prev = q;
      }
      const n = Math.min(256, Math.max(2, Math.ceil(len / spacing)));
      for (let k = 1; k <= n; k++) {
        const q = map(segmentPoint(seg, k / n));
        out.push(VERB.LINE, q.x, q.y);
      }
    }
    if (contour.closed) out.push(VERB.CLOSE);
  }
  return out;
}

/** Index into `points` of lattice point (i, j). */
export const latticeIndex = (w: StoredMesh, i: number, j: number) => j * (3 * w.cols + 1) + i;
